import { CORRECT_CODE, MESSAGE, REQUEST, RESPONSE, UNKNOWN_CODE, } from "./constants";
import { generateNanoid } from "./utils";
/**
 * use to communicate between browser (runtime-connector) and extension (dataverse-kernel)
 */
export class Communicator {
    constructor({ source, target, methodClass, postMessageTo, }) {
        this.allowOrigins = "*";
        this.destroyed = false;
        this.sequenceId = generateNanoid();
        this.callbackFunctions = {};
        this.responseSequenceIds = {};
        this.targetOrigin = target;
        this.sourceOrigin = source;
        this.methodClass = methodClass;
        this.postMessageTo = postMessageTo;
        this.sourceOrigin.addEventListener(MESSAGE, this._onmessage.bind(this));
    }
    setPostMessageTo(postMessageTo) {
        this.postMessageTo = postMessageTo;
    }
    async sendRequest(args) {
        if (this.destroyed)
            return;
        return new Promise((resolve, reject) => {
            this.targetOrigin.postMessage({
                sequenceId: this.sequenceId,
                type: REQUEST,
                method: args.method,
                params: args.params,
                postMessageTo: this.postMessageTo,
            }, this.allowOrigins);
            this.callbackFunctions[this.sequenceId] = (res) => {
                if (res.code === CORRECT_CODE) {
                    resolve(res.result);
                }
                else {
                    reject(res.error);
                }
            };
            this.sequenceId = generateNanoid();
        });
    }
    sendResponse(args) {
        if (this.destroyed)
            return;
        this.targetOrigin.postMessage({
            sequenceId: args.sequenceId,
            type: RESPONSE,
            result: args.result,
        }, args.origin);
        this.responseSequenceIds[args.sequenceId] = true;
    }
    async _onmessage(event) {
        if (this.destroyed)
            return;
        if (event.data.type === RESPONSE) {
            const args = event.data;
            // When this class sends a response, it does not need to return data when this class listens to it
            if (this.responseSequenceIds[args.sequenceId]) {
                return;
            }
            if (this.handleRequestMessage) {
                this.handleRequestMessage(event);
            }
            else {
                const callbackFunc = this.callbackFunctions[args.sequenceId];
                callbackFunc(args.result);
            }
        }
        else if (event.data.type === REQUEST) {
            const args = event.data;
            // When this class sends a request, it does not need to return data when this class listens to it
            if (this.callbackFunctions[args.sequenceId]) {
                return;
            }
            // if code running env is different from postMessageTo, it will return and do nothing
            if (process.env.ENV !== args.postMessageTo) {
                return;
            }
            if (this.handleResponseMessage) {
                this.handleResponseMessage(event);
            }
            else {
                let result;
                if (!this.methodClass) {
                    result = {
                        code: UNKNOWN_CODE,
                        error: "Please pass in the methodClass, in order to call methods in the class",
                    };
                }
                else {
                    try {
                        const res = await this.methodClass[args.method](args.params);
                        result = { code: CORRECT_CODE, result: res };
                    }
                    catch (error) {
                        result = {
                            code: error?.code || UNKNOWN_CODE,
                            error: error?.msg || error?.message,
                        };
                    }
                }
                this.sendResponse({
                    sequenceId: args.sequenceId,
                    type: RESPONSE,
                    result,
                    origin: event.origin,
                });
            }
        }
    }
    // customize
    onRequestMessage(fn) {
        this.handleResponseMessage = fn;
    }
    // customize
    onResponseMessage(fn) {
        this.handleRequestMessage = fn;
    }
    destroy() {
        this.destroyed = true;
        this.sourceOrigin.removeEventListener(MESSAGE, this._onmessage.bind(this));
    }
}
